import { useEffect, useRef, useState, type ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

import { useAuth } from "auth";
import { DirectUploadError } from "lib/direct-upload";
import {
  buildWatermarkInput,
  checkWatermarkLogoFile,
  fetchGalleryWatermark,
  galleryErrorMessage,
  removeGalleryWatermarkLogoRequest,
  showsStoredLogoOnly,
  updateGalleryWatermarkRequest,
  uploadGalleryWatermarkLogoRequest,
  watermarkForm,
  watermarkPreviewOpacity,
  watermarkProfileName,
  WATERMARK_KINDS,
  WATERMARK_POSITIONS,
  WATERMARK_STRENGTHS,
  WATERMARK_TEXT_MAX_LENGTH,
  type GalleryWatermark,
  type WatermarkForm,
  type WatermarkPosition,
  type WatermarkStrength,
} from "gallery";

import {
  ConfirmDialog,
  FieldLabel,
  notifyGalleryError,
  notifyGallerySuccess,
  useGalleryText,
} from "./gallery-ui";
import { PlanLockBadge } from "./plan-lock-badge";

interface GalleryWatermarkDialogProps {
  token?: string;
  onClose: () => void;
}

// The Account's Watermark template (spec decision 12), one for every gallery; each gallery switches
// it on in its Design view. "Done" saves what changed; a logo uploads (and becomes the mark) at once.
// Without the Plan the template shows, disabled, with the Pro badge.
export function GalleryWatermarkDialog({ token, onClose }: GalleryWatermarkDialogProps) {
  const { t } = useGalleryText();
  const w = t.watermark;
  const { activeProfile } = useAuth();
  const profileName = watermarkProfileName(activeProfile);
  const [template, setTemplate] = useState<GalleryWatermark | null>(null);
  const [form, setForm] = useState<WatermarkForm | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState<"saving" | "uploading" | "removing" | null>(null);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const apply = (next: GalleryWatermark, keepForm?: Partial<WatermarkForm>) => {
    setTemplate(next);
    setForm({ ...watermarkForm(next), ...keepForm });
  };

  useEffect(() => {
    let active = true;
    fetchGalleryWatermark(token)
      .then((next) => active && apply(next))
      .catch(() => active && setLoadFailed(true));
    return () => {
      active = false;
    };
  }, [token]);

  const locked = template ? !template.canChange : false;
  const disabled = locked || busy !== null;
  const set = <K extends keyof WatermarkForm>(key: K, value: WatermarkForm[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  const save = async () => {
    if (!template || !form) {
      return;
    }
    const input = buildWatermarkInput(template, form);
    if (!Object.keys(input).length) {
      onClose();
      return;
    }
    setBusy("saving");
    try {
      await updateGalleryWatermarkRequest(input, token);
      notifyGallerySuccess(w.title, w.saved);
      onClose();
    } catch (error) {
      notifyGalleryError(w.title, galleryErrorMessage(error, t.errors));
      setBusy(null);
    }
  };

  const uploadLogo = async (file: File | undefined) => {
    if (!file || !form) {
      return;
    }
    const refusal = checkWatermarkLogoFile(file);
    if (refusal) {
      notifyGalleryError(w.title, w.logoErrors[refusal]);
      return;
    }
    setBusy("uploading");
    try {
      await uploadGalleryWatermarkLogoRequest(file, token);
      // The text, position and strength typed so far stay as typed.
      apply(await fetchGalleryWatermark(token), {
        text: form.text,
        position: form.position,
        strength: form.strength,
      });
      notifyGallerySuccess(w.title, w.logoUploaded);
    } catch (error) {
      const code = error instanceof DirectUploadError ? error.code : undefined;
      notifyGalleryError(
        w.title,
        code === "GALLERY_PLAN_REQUIRED"
          ? t.errors.GALLERY_PLAN_REQUIRED
          : code === "STORAGE_LIMIT_EXCEEDED"
          ? w.logoErrors.STORAGE_LIMIT_EXCEEDED
          : w.logoErrors.FAILED
      );
    } finally {
      setBusy(null);
    }
  };

  const removeLogo = async () => {
    if (!form) {
      return;
    }
    setBusy("removing");
    try {
      apply(await removeGalleryWatermarkLogoRequest(token), {
        text: form.text,
        position: form.position,
        strength: form.strength,
      });
      notifyGallerySuccess(w.title, w.logoRemoved);
    } catch (error) {
      notifyGalleryError(w.title, galleryErrorMessage(error, t.errors));
    } finally {
      setBusy(null);
      setIsRemoveOpen(false);
    }
  };

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="gallery-watermark-dialog"
    >
      <DialogTitle>{w.title}</DialogTitle>
      <DialogContent>
        {!template || !form ? (
          <SoftTypography variant="button" fontWeight="regular" color="text">
            {loadFailed ? w.loadFailed : t.loading}
          </SoftTypography>
        ) : (
          <SoftBox display="flex" flexDirection="column" gap={2} pt={1}>
            {locked ? (
              <SoftBox
                display="flex"
                alignItems="center"
                gap={1}
                data-testid="gallery-watermark-locked"
              >
                <PlanLockBadge testId="gallery-watermark-lock" />
                <SoftTypography variant="caption" color="text">
                  {w.locked}
                </SoftTypography>
              </SoftBox>
            ) : null}
            <WatermarkPreview
              label={w.preview}
              form={form}
              template={template}
              profileName={profileName}
            />
            <SoftTypography variant="caption" color="text">
              {w.previewHint}
            </SoftTypography>

            <Choice label={w.kindLabel}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={form.kind}
                disabled={disabled}
                onChange={(_event, kind) => kind && set("kind", kind)}
                data-testid="gallery-watermark-kind"
              >
                {WATERMARK_KINDS.map((kind) => (
                  <ToggleButton
                    key={kind}
                    value={kind}
                    data-testid={`gallery-watermark-kind-${kind}`}
                  >
                    {w.kinds[kind]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Choice>

            {form.kind === "TEXT" ? (
              <SoftBox>
                <FieldLabel htmlFor="gallery-watermark-text">{w.textLabel}</FieldLabel>
                <SoftInput
                  id="gallery-watermark-text"
                  value={form.text}
                  disabled={disabled}
                  placeholder={profileName || w.textPlaceholder}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    set("text", event.target.value)
                  }
                  inputProps={{
                    maxLength: WATERMARK_TEXT_MAX_LENGTH,
                    "data-testid": "gallery-watermark-text",
                  }}
                />
                <SoftTypography variant="caption" color="text">
                  {w.textHint}
                </SoftTypography>
                {showsStoredLogoOnly(template, form.kind) ? (
                  <SoftBox mt={2} data-testid="gallery-watermark-stored-logo">
                    <FieldLabel>{w.logoLabel}</FieldLabel>
                    <SoftBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      {template.logoUrl ? (
                        <img
                          src={template.logoUrl}
                          alt={template.logoFileName ?? w.logoLabel}
                          style={{ maxWidth: 48, maxHeight: 48 }}
                          data-testid="gallery-watermark-logo"
                        />
                      ) : null}
                      <SoftTypography variant="button" fontWeight="regular" sx={{ flex: 1 }}>
                        {template.logoFileName}
                      </SoftTypography>
                      <SoftButton
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={busy !== null}
                        onClick={() => setIsRemoveOpen(true)}
                        data-testid="gallery-watermark-remove-logo"
                      >
                        {w.removeLogo}
                      </SoftButton>
                    </SoftBox>
                    <SoftTypography variant="caption" color="text">
                      {w.storedLogoHint}
                    </SoftTypography>
                  </SoftBox>
                ) : null}
              </SoftBox>
            ) : (
              <SoftBox>
                <FieldLabel>{w.logoLabel}</FieldLabel>
                <SoftBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <SoftBox
                    width={56}
                    height={56}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      background:
                        "repeating-conic-gradient(#e9ecef 0% 25%, #fff 0% 50%) 50% / 12px 12px",
                    }}
                  >
                    {template.hasLogo && template.logoUrl ? (
                      <img
                        src={template.logoUrl}
                        alt={template.logoFileName ?? w.logoLabel}
                        style={{ maxWidth: 48, maxHeight: 48 }}
                        data-testid="gallery-watermark-logo"
                      />
                    ) : null}
                  </SoftBox>
                  <SoftTypography variant="button" fontWeight="regular" sx={{ flex: 1 }}>
                    {busy === "uploading"
                      ? w.uploading
                      : template.hasLogo
                      ? template.logoFileName
                      : w.noLogo}
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="info"
                    size="small"
                    disabled={disabled}
                    onClick={() => fileInput.current?.click()}
                    data-testid="gallery-watermark-choose-logo"
                  >
                    {template.hasLogo ? w.replaceLogo : w.chooseLogo}
                  </SoftButton>
                  {template.hasLogo ? (
                    <SoftButton
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={busy !== null}
                      onClick={() => setIsRemoveOpen(true)}
                      data-testid="gallery-watermark-remove-logo"
                    >
                      {w.removeLogo}
                    </SoftButton>
                  ) : null}
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/png"
                    hidden
                    data-testid="gallery-watermark-logo-input"
                    onChange={(event) => {
                      void uploadLogo(event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                </SoftBox>
                <SoftTypography variant="caption" color="text">
                  {w.logoHint}
                </SoftTypography>
              </SoftBox>
            )}

            <Choice label={w.positionLabel}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={form.position}
                disabled={disabled}
                onChange={(_event, position) => position && set("position", position)}
              >
                {WATERMARK_POSITIONS.map((position) => (
                  <ToggleButton
                    key={position}
                    value={position}
                    aria-label={w.positions[position]}
                    data-testid={`gallery-watermark-position-${position}`}
                    sx={{ flexDirection: "column", gap: 0.5, px: 2 }}
                  >
                    <PositionPictogram position={position} />
                    {w.positions[position]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Choice>

            <Choice label={w.strengthLabel}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={form.strength}
                disabled={disabled}
                onChange={(_event, strength) => strength && set("strength", strength)}
              >
                {WATERMARK_STRENGTHS.map((strength) => (
                  <ToggleButton
                    key={strength}
                    value={strength}
                    aria-label={w.strengths[strength]}
                    data-testid={`gallery-watermark-strength-${strength}`}
                    sx={{ flexDirection: "column", gap: 0.5, px: 2 }}
                  >
                    <StrengthPictogram strength={strength} />
                    {w.strengths[strength]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Choice>

            <SoftTypography variant="caption" color="text">
              {w.appliesHint}
            </SoftTypography>
          </SoftBox>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <SoftButton variant="outlined" color="secondary" onClick={onClose} disabled={busy !== null}>
          {t.cancel}
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="info"
          onClick={save}
          disabled={!template || disabled}
          data-testid="gallery-watermark-done"
        >
          {busy === "saving" ? w.saving : w.done}
        </SoftButton>
      </DialogActions>
      <ConfirmDialog
        open={isRemoveOpen}
        title={w.removeLogoTitle}
        body={w.removeLogoBody}
        confirmLabel={w.removeLogoConfirm}
        cancelLabel={t.cancel}
        busy={busy === "removing"}
        testId="gallery-watermark-remove-logo-dialog"
        onCancel={() => setIsRemoveOpen(false)}
        onConfirm={removeLogo}
      />
    </Dialog>
  );
}

function Choice({ label, children }: { label: string; children: ReactNode }) {
  return (
    <SoftBox>
      <FieldLabel>{label}</FieldLabel>
      <SoftBox>{children}</SoftBox>
    </SoftBox>
  );
}

const FRAME = {
  width: 40,
  height: 28,
  borderRadius: 4,
  background: "#dee2e6",
  position: "relative" as const,
};
const BAR = { width: 12, height: 3, borderRadius: 2, background: "#344767" };

// A small photo frame with the mark's place drawn as bars.
function PositionPictogram({ position }: { position: WatermarkPosition }) {
  if (position === "TILED") {
    return (
      <span
        style={{
          ...FRAME,
          display: "flex",
          flexWrap: "wrap",
          alignContent: "space-around",
          justifyContent: "space-around",
          padding: 3,
        }}
      >
        {Array.from({ length: 6 }, (_, index) => (
          <span key={index} style={{ ...BAR, opacity: 0.6 }} />
        ))}
      </span>
    );
  }
  return (
    <span
      style={{
        ...FRAME,
        display: "flex",
        padding: 4,
        alignItems: position === "CENTER" ? "center" : "flex-end",
        justifyContent: position === "CENTER" ? "center" : "flex-end",
      }}
    >
      <span style={BAR} />
    </span>
  );
}

function StrengthPictogram({ strength }: { strength: WatermarkStrength }) {
  return (
    <span style={{ ...FRAME, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span
        style={{
          width: 24,
          height: 12,
          borderRadius: 2,
          background: "#344767",
          opacity: watermarkPreviewOpacity(strength),
        }}
      />
    </span>
  );
}

// A sample photo (sky, hills, sun drawn in CSS) with the mark roughly where and how strong the real
// one will be.
function WatermarkPreview({
  label,
  form,
  template,
  profileName,
}: {
  label: string;
  form: WatermarkForm;
  template: GalleryWatermark;
  profileName: string;
}) {
  const opacity = watermarkPreviewOpacity(form.strength);
  const tiled = form.position === "TILED";
  const logoUrl = form.kind === "LOGO" && template.hasLogo ? template.logoUrl : null;
  const text = form.text.trim() || profileName;
  const mark = (key: number) =>
    logoUrl ? (
      <img
        key={key}
        src={logoUrl}
        alt=""
        style={{ maxWidth: tiled ? 64 : 120, maxHeight: tiled ? 32 : 60, opacity }}
      />
    ) : (
      <span
        key={key}
        style={{
          color: "#fff",
          fontWeight: 600,
          fontSize: tiled ? 12 : 18,
          opacity,
          whiteSpace: "nowrap",
          transform: tiled ? "rotate(-20deg)" : undefined,
        }}
      >
        {text}
      </span>
    );

  return (
    <SoftBox
      role="img"
      aria-label={label}
      data-testid="gallery-watermark-preview"
      data-position={form.position}
      data-strength={form.strength}
      sx={{
        position: "relative",
        aspectRatio: "4 / 3",
        maxHeight: 280,
        borderRadius: 2,
        overflow: "hidden",
        background:
          "radial-gradient(circle at 78% 22%, #e7d7a8 0 7%, transparent 7.5%), radial-gradient(ellipse at 25% 85%, #5c6f55 0 38%, transparent 38.5%), linear-gradient(#6b7d8c 0 62%, #4b5d4a 62% 100%)",
      }}
    >
      <SoftBox
        sx={{
          position: "absolute",
          inset: 0,
          p: 2,
          display: "flex",
          flexWrap: tiled ? "wrap" : "nowrap",
          alignItems: tiled ? "center" : form.position === "CENTER" ? "center" : "flex-end",
          alignContent: "space-around",
          justifyContent: tiled
            ? "space-around"
            : form.position === "CENTER"
            ? "center"
            : "flex-end",
        }}
      >
        {tiled
          ? Array.from({ length: 9 }, (_, index) => (
              <SoftBox key={index} width="33%" textAlign="center">
                {mark(index)}
              </SoftBox>
            ))
          : mark(0)}
      </SoftBox>
    </SoftBox>
  );
}
